import { useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, DollarSign, Users, Award, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Recruitment = ({ onApply }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    experience: "",
    motivation: "",
  });

  const benefits = [
    {
      icon: DollarSign,
      title: "Uncapped Earnings",
      description: "No limits on your income potential. Your success is in your hands.",
    },
    {
      icon: Users,
      title: "Build Your Team",
      description: "Create your own agency and build generational wealth.",
    },
    {
      icon: Briefcase,
      title: "Be Your Own Boss",
      description: "Flexible schedule with the freedom to work on your terms.",
    },
    {
      icon: Award,
      title: "Full Training",
      description: "Comprehensive training and ongoing support to help you succeed.",
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, experience: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const success = await onApply(formData);
    setIsSubmitting(false);
    if (success) {
      setFormData({
        name: "",
        email: "",
        phone: "",
        location: "",
        experience: "",
        motivation: "",
      });
      setIsModalOpen(false);
    }
  };

  return (
    <section
      id="recruitment"
      className="py-24 bg-navy pattern-bg relative overflow-hidden"
      data-testid="recruitment-section"
    >
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-1 bg-amber-500 mb-6" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 font-serif">
              Join the Crusade for{" "}
              <span className="text-amber-500">Financial Literacy</span>
            </h2>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              Build your own agency with our proven business model. We provide
              the training, resources, and support you need to achieve financial
              independence and help others do the same.
            </p>

            {/* Benefits Grid */}
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-start gap-4"
                  data-testid={`recruitment-benefit-${index}`}
                >
                  <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">
                      {benefit.title}
                    </h4>
                    <p className="text-sm text-slate-400">
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Button
              onClick={() => setIsModalOpen(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-200 hover:shadow-xl"
              data-testid="recruitment-cta-button"
            >
              Apply to Join Our Team
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>

          {/* Right - Stats/Testimonial */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <div className="text-center mb-8">
                <div className="text-6xl font-bold text-amber-500 mb-2">
                  $100K+
                </div>
                <div className="text-slate-300">
                  Average first-year earnings potential
                </div>
              </div>

              <div className="section-divider mb-8" />

              <blockquote className="text-slate-200 text-lg italic mb-6">
                "WealthConnexs gave me the tools and support to build my own
                business. In just two years, I've achieved financial independence
                and helped hundreds of families protect their futures."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <span className="text-amber-500 font-bold">JD</span>
                </div>
                <div>
                  <div className="font-semibold text-white">James D.</div>
                  <div className="text-sm text-slate-400">
                    Agency Owner, 2 Years
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Application Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="recruitment-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif text-navy">
              Join Our Team
            </DialogTitle>
            <DialogDescription>
              Take the first step toward financial independence. Fill out the
              form below and we'll be in touch.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="John Doe"
                  className="h-12"
                  data-testid="recruitment-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="john@example.com"
                  className="h-12"
                  data-testid="recruitment-email-input"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="(555) 123-4567"
                  className="h-12"
                  data-testid="recruitment-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  placeholder="City, State/Province"
                  className="h-12"
                  data-testid="recruitment-location-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="experience">Experience Level</Label>
              <Select onValueChange={handleSelectChange} value={formData.experience}>
                <SelectTrigger className="h-12" data-testid="recruitment-experience-select">
                  <SelectValue placeholder="Select your experience" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No experience</SelectItem>
                  <SelectItem value="1-2">1-2 years</SelectItem>
                  <SelectItem value="3-5">3-5 years</SelectItem>
                  <SelectItem value="5+">5+ years</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="motivation">Why do you want to join? *</Label>
              <Textarea
                id="motivation"
                name="motivation"
                value={formData.motivation}
                onChange={handleInputChange}
                required
                placeholder="Tell us about your goals and why you're interested in financial services..."
                className="min-h-[100px] resize-none"
                data-testid="recruitment-motivation-textarea"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-navy hover:bg-slate-800 text-white h-12 rounded-full font-medium"
              data-testid="recruitment-submit-button"
            >
              {isSubmitting ? "Submitting..." : "Submit Application"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Recruitment;
